// LGPD consent gate — bridges the Termly consent banner (loaded in
// index.html, website 448d3cdf-4465-468e-95fe-7c0e8beba29c, same vendor as
// the /privacy embed) to the three consent-aware vendors:
//   advertising → Meta Pixel/CAPI (setMetaConsent) + OneSignal push SDK
//   analytics   → PostHog (opt_in/opt_out_capturing)
//
// PRE-DECISION STATE (documented): before the visitor interacts with the
// banner there is NO stored Termly decision — getConsent() then returns the
// PRE_DECISION defaults below: GRANTED. Rationale: Termly's region-aware
// banner for Brazil runs a notice-first model (banner shows, non-essential
// scripts are not auto-blocked pre-decision; hard opt-in auto-block is the
// EU/GDPR preset), and this mirrors the app's pre-banner behavior exactly —
// an explicit "Decline" is what revokes. To switch Brazil to hard opt-in
// later, flip PRE_DECISION_GRANTED to false — every consumer already handles
// the grant-later transition.
//
// ⚠️ Termly's JS API surface could NOT be verified from this build
// environment (app.termly.io and its docs are proxy-blocked), so change
// detection is DEFENSIVE, in layers, none of which throw when absent:
//   1. window.Termly.on("consent", cb) — feature-detected, used when exposed.
//   2. localStorage TERMLY_API_CACHE — Termly's long-stable persistence key;
//      parsed defensively, polled at a slow interval + on tab focus, plus
//      the cross-tab `storage` event.
// Validation on the real site before 15/07 is REQUIRED (see PR checklist).

export interface ConsentState {
  analytics: boolean;
  advertising: boolean;
}

const PRE_DECISION_GRANTED = true;
const TERMLY_STORAGE_KEY = "TERMLY_API_CACHE";
const POLL_MS = 4000;

declare global {
  interface Window {
    Termly?: {
      on?: (event: string, cb: (state: unknown) => void) => void;
      getConsentState?: () => unknown;
    };
  }
}

function defaults(): ConsentState {
  return { analytics: PRE_DECISION_GRANTED, advertising: PRE_DECISION_GRANTED };
}

// Pull {analytics, advertising} out of whatever shape Termly hands us —
// their cache nests the category booleans under varying wrappers, so walk
// the object for the first node that carries the known category keys.
function extractCategories(node: unknown, depth = 0): ConsentState | null {
  if (!node || typeof node !== "object" || depth > 4) return null;
  const obj = node as Record<string, unknown>;
  if (typeof obj.analytics === "boolean" || typeof obj.advertising === "boolean") {
    return {
      analytics: obj.analytics !== false,
      advertising: obj.advertising !== false,
    };
  }
  for (const value of Object.values(obj)) {
    const found = extractCategories(value, depth + 1);
    if (found) return found;
  }
  return null;
}

// Current consent: the visitor's stored Termly decision, or the documented
// pre-decision defaults when none exists (or parsing fails — logged).
export function getConsent(): ConsentState {
  try {
    const raw = localStorage.getItem(TERMLY_STORAGE_KEY);
    if (!raw) return defaults();
    const parsed = extractCategories(JSON.parse(raw));
    return parsed ?? defaults();
  } catch (e) {
    console.error("[consent] failed to read Termly consent — using defaults:", e);
    return defaults();
  }
}

// Subscribe to consent changes. Fires cb only when a category actually
// flips. Returns nothing on purpose — this lives for the page lifetime.
export function onConsentChange(cb: (state: ConsentState) => void): void {
  let last = JSON.stringify(getConsent());
  const check = () => {
    const current = getConsent();
    const key = JSON.stringify(current);
    if (key !== last) {
      last = key;
      console.log("[consent] consent changed:", current);
      cb(current);
    }
  };

  // Layer 1 — Termly's own event, when the runtime exposes it.
  try {
    if (typeof window.Termly?.on === "function") {
      window.Termly.on("consent", check);
    }
  } catch (e) {
    console.error("[consent] Termly.on hook failed:", e);
  }
  // Layer 2 — storage event (fires cross-tab; some CMP writes trigger it).
  window.addEventListener("storage", (ev) => {
    if (ev.key === TERMLY_STORAGE_KEY) check();
  });
  // Layer 3 — slow poll + focus recheck (same-tab writes don't fire
  // `storage`; the banner interaction happens in THIS tab).
  window.setInterval(check, POLL_MS);
  window.addEventListener("focus", check);
}
